import {
  DOCUMENTS_STORE,
  getDb,
  promisifyRequest,
  promisifyTxn,
} from "./PdfDb";
import type { DocumentRecord, PdfDocState, UploadedMainFile } from "./types";

export async function putDocument(
  id: string,
  originalBytes: Uint8Array,
): Promise<void> {
  const db = await getDb();
  const txn = db.transaction(DOCUMENTS_STORE, "readwrite");
  const record: DocumentRecord = {
    id,
    originalBytes,
    docState: null,
    updatedAt: Date.now(),
  };
  txn.objectStore(DOCUMENTS_STORE).put(record);
  await promisifyTxn(txn);
}

export async function getDocument(
  id: string,
): Promise<DocumentRecord | undefined> {
  const db = await getDb();
  const txn = db.transaction(DOCUMENTS_STORE, "readonly");
  return promisifyRequest(txn.objectStore(DOCUMENTS_STORE).get(id));
}

/**
 * Persist the canonical PdfDocState atomically. Overwrites prior state —
 * per-edit undo/redo lives in EmbedPDF's plugin-history (in-memory, reset
 * on reload); rewind-to-message lives in the checkpoints store.
 */
export async function putDocState(
  id: string,
  docState: PdfDocState,
): Promise<void> {
  const db = await getDb();
  const txn = db.transaction(DOCUMENTS_STORE, "readwrite");
  const store = txn.objectStore(DOCUMENTS_STORE);
  const existing = (await promisifyRequest(store.get(id))) as
    | DocumentRecord
    | undefined;
  if (!existing) return;
  store.put({
    ...existing,
    docState,
    updatedAt: Date.now(),
  });
  await promisifyTxn(txn);
}

/**
 * Cache the backend upload metadata on the document record. Lets future
 * chat rotations reuse the same Firebase object via the `mainFile` body
 * field instead of re-streaming the PDF.
 */
export async function putMainFile(
  id: string,
  mainFile: UploadedMainFile,
): Promise<void> {
  const db = await getDb();
  const txn = db.transaction(DOCUMENTS_STORE, "readwrite");
  const store = txn.objectStore(DOCUMENTS_STORE);
  const existing = (await promisifyRequest(store.get(id))) as
    | DocumentRecord
    | undefined;
  if (!existing) return;
  store.put({
    ...existing,
    mainFile,
    updatedAt: Date.now(),
  });
  await promisifyTxn(txn);
}
